import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { invalidateCuring } from './curing';
import { useContext, useEffect } from 'react';
import { api } from '../utils/api';
import { ApiResponse, ApiError } from '../types/api';
import { Vegetation } from '../types/vegetation';
import { useParams } from 'react-router-dom';
import { StrategyContext } from '../contexts/StrategyContext';

interface VegetationDetailProperties {
  VegClass: string;
  lmax: number | null;
  k: number | null;
  model_class: number | null;
  model_class_name: string | null;
  model_subclass: number | null;
  lmax_overridden: boolean;
  k_overridden: boolean;
  model_class_overridden: boolean;
  model_subclass_overridden: boolean;
  id: number;
}

interface VegetationDetailFeature {
  id: number;
  type: string;
  properties: VegetationDetailProperties;
  geometry: any;
}

export const useVegetationDetail = (vegId: number | null, enabled: boolean) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery<VegetationDetailProperties | null, ApiError>({
    queryKey: ['vegetation-detail', projectId, vegId],
    enabled: Boolean(projectId && vegId && enabled),
    queryFn: async () => {
      if (!projectId || vegId == null) return null;
      const response = await api.get<ApiResponse<VegetationDetailFeature>>(
        `/api/projects/${projectId}/vegetations/${vegId}`
      );
      return response.data.properties;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

export const useVegetationTileVersion = (
  projectId: number | string | undefined,
  projectUpdated?: string | Date | null,
) => {
  const queryClient = useQueryClient();
  const projectKey = projectId !== undefined && projectId !== null ? String(projectId) : undefined;

  useEffect(() => {
    if (!projectKey) return;

    const initialVersion = projectUpdated
      ? new Date(projectUpdated).getTime()
      : Date.now();

    queryClient.setQueryData<number | undefined>(
      ['vegetation-tile-version', projectKey],
      (existing) => (existing !== undefined ? existing : initialVersion),
    );
  }, [projectKey, projectUpdated, queryClient]);

  const { data } = useQuery<number | undefined>({
    queryKey: ['vegetation-tile-version', projectKey],
    enabled: Boolean(projectKey),
    queryFn: () => {
      if (!projectKey) return undefined;
      const cached = queryClient.getQueryData<number>(['vegetation-tile-version', projectKey]);
      if (cached !== undefined) {
        return cached;
      }
      const fallback = Date.now();
      queryClient.setQueryData(['vegetation-tile-version', projectKey], fallback);
      return fallback;
    },
    staleTime: Infinity,
  });

  return data;
};

export const bumpVegetationTileVersion = (queryClient: ReturnType<typeof useQueryClient>, projectId: string | undefined) => {
  if (!projectId) return;
  queryClient.setQueryData<number>(
    ['vegetation-tile-version', projectId],
    () => Date.now()
  );
};

interface UpdateVegetationClassData {
  id: number;
  veg_class: string;
}

interface UpdateModelClassData {
  id: number;
  model_class_name: string | null;
}

interface UpdateFuelParametersData {
  id: number;
  max_fuel_load: number | null;
  accumulation_rate: number | null;
}

interface UpdateModelSubclassData {
  id: number;
  model_subclass: number | null;
}

export const useUpdateVegetationClass = () => {
  const queryClient = useQueryClient();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectKey = projectIdParam ?? undefined;
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<Vegetation>, ApiError, UpdateVegetationClassData>({
    mutationFn: async (data: UpdateVegetationClassData) => {
      return await api.request<ApiResponse<Vegetation>>(
        `/api/projects/${projectIdParam}/vegetations`,
        {
          method: 'PATCH',
          body: { id: data.id, veg_class: data.veg_class },
        }
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refetch data after successful update
      queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
      invalidateCuring(queryClient, projectKey);
      // The strategy job/status queries key on 'strategyJob'/'strategyStatus'
      // (see StrategyContext) — a bare 'strategy' prefix matches neither.
      queryClient.invalidateQueries({ queryKey: ['strategyJob'] });
      queryClient.invalidateQueries({ queryKey: ['strategyStatus'] });
      refreshStrategy();
      if (projectKey) {
        bumpVegetationTileVersion(queryClient, projectKey);
        queryClient.invalidateQueries({ queryKey: ['vegetation-detail', projectKey, variables.id] });
      }
    },
  });
};

export const useUpdateModelClass = () => {
  const queryClient = useQueryClient();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectKey = projectIdParam ?? undefined;
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<Vegetation>, ApiError, UpdateModelClassData>({
    mutationFn: async (data: UpdateModelClassData) => {
      return await api.request<ApiResponse<Vegetation>>(
        `/api/projects/${projectIdParam}/vegetations`,
        {
          method: 'PATCH',
          body: { id: data.id, model_class_name: data.model_class_name },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
      invalidateCuring(queryClient, projectKey);
      // The strategy job/status queries key on 'strategyJob'/'strategyStatus'
      // (see StrategyContext) — a bare 'strategy' prefix matches neither.
      queryClient.invalidateQueries({ queryKey: ['strategyJob'] });
      queryClient.invalidateQueries({ queryKey: ['strategyStatus'] });
      refreshStrategy();
      if (projectKey) {
        bumpVegetationTileVersion(queryClient, projectKey);
        queryClient.invalidateQueries({ queryKey: ['vegetation-detail', projectKey, variables.id] });
      }
    },
  });
};

export const useUpdateModelSubclass = () => {
  const queryClient = useQueryClient();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectKey = projectIdParam ?? undefined;
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<Vegetation>, ApiError, UpdateModelSubclassData>({
    mutationFn: async (data: UpdateModelSubclassData) => {
      return await api.request<ApiResponse<Vegetation>>(
        `/api/projects/${projectIdParam}/vegetations`,
        {
          method: 'PATCH',
          body: { id: data.id, model_subclass: data.model_subclass },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
      invalidateCuring(queryClient, projectKey);
      // The strategy job/status queries key on 'strategyJob'/'strategyStatus'
      // (see StrategyContext) — a bare 'strategy' prefix matches neither.
      queryClient.invalidateQueries({ queryKey: ['strategyJob'] });
      queryClient.invalidateQueries({ queryKey: ['strategyStatus'] });
      refreshStrategy();
      if (projectKey) {
        bumpVegetationTileVersion(queryClient, projectKey);
        queryClient.invalidateQueries({ queryKey: ['vegetation-detail', projectKey, variables.id] });
      }
    },
  });
};

export const useUpdateFuelParameters = () => {
  const queryClient = useQueryClient();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectKey = projectIdParam ?? undefined;
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<Vegetation>, ApiError, UpdateFuelParametersData>({
    mutationFn: async (data: UpdateFuelParametersData) => {
      return await api.request<ApiResponse<Vegetation>>(
        `/api/projects/${projectIdParam}/vegetations`,
        {
          method: 'PATCH',
          body: { 
            id: data.id, 
            max_fuel_load: data.max_fuel_load, 
            accumulation_rate: data.accumulation_rate 
          },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
      invalidateCuring(queryClient, projectKey);
      // The strategy job/status queries key on 'strategyJob'/'strategyStatus'
      // (see StrategyContext) — a bare 'strategy' prefix matches neither.
      queryClient.invalidateQueries({ queryKey: ['strategyJob'] });
      queryClient.invalidateQueries({ queryKey: ['strategyStatus'] });
      refreshStrategy();
      if (projectKey) {
        bumpVegetationTileVersion(queryClient, projectKey);
        queryClient.invalidateQueries({ queryKey: ['vegetation-detail', projectKey, variables.id] });
      }
    },
  });
};
